import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Copy, Target, Users, Calendar, Briefcase, MessageSquare, Shield, CheckCircle, BookOpen } from 'lucide-react';

const ScriptCard = ({ title, content, category }) => {
  const copyToClipboard = () => {
    navigator.clipboard.writeText(content);
    toast.success('Script copied to clipboard!');
  };

  return (
    <Card className="p-4 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold text-[#333333]">{title}</h4>
          {category && <Badge variant="outline" className="mt-1 text-xs">{category}</Badge>}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={copyToClipboard}
          className="flex items-center gap-1"
          data-testid={`copy-script-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <Copy className="h-4 w-4" />
          Copy
        </Button>
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-line">{content}</p>
    </Card>
  );
};

const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-2">
      <Icon className="h-6 w-6 text-[#E31837]" />
      <h2 className="text-2xl font-bold text-[#333333]">{title}</h2>
    </div>
    {description && <p className="text-gray-600">{description}</p>}
  </div>
);

export const PlaybookLibrary = () => {
  const [activeTab, setActiveTab] = useState('prospecting');

  const prospectingContent = {
    definitions: [
      { term: 'Hot', description: 'People actively interested, ready to take action now. They\'ve expressed direct interest or urgency.' },
      { term: 'Warm', description: 'People who know you and are open to conversations. Friends, family, acquaintances with existing trust.' },
      { term: 'Cultivated', description: 'Contacts you\'ve been nurturing over time. They\'re aware of what you do and may be warming up.' },
      { term: 'Cold', description: 'New contacts with no prior relationship. Require more trust-building before business discussions.' },
      { term: 'Business Owner', description: 'Entrepreneurs, professionals, and self-employed individuals. Higher income potential and referral networks.' }
    ],
    framework: `THE HUNT/FARM/CULTIVATE FRAMEWORK

Goal: 50 messages per day, cycling through your entire contact list annually.

HUNT (Direct Ask - New Contacts)
• Direct outreach to new or lightly-contacted prospects
• Clear call-to-action: appointment, call, meeting
• Focus: "I'd love to share something that might help you"

FARM (Indirect Ask - Referrals)
• Reach out to existing contacts for referrals
• Ask: "Who do you know that might benefit from..."
• Focus: Building your network through warm introductions

CULTIVATE (No-Agenda Touch)
• Relationship maintenance with no business ask
• Birthday wishes, congratulations, check-ins
• Focus: Building trust and staying top-of-mind

Cycle: Move each contact through Hunt → Farm → Cultivate, then repeat annually.`,
    scripts: [
      {
        title: 'Hunt Script - Direct Approach',
        content: `Hey [Name]! Hope you're doing well.

I wanted to reach out because I've been helping families protect their income and build financial security. 

I'd love to share some strategies that might be valuable for you - it only takes about 20 minutes.

Would Tuesday or Wednesday evening work better for a quick call?`
      },
      {
        title: 'Farm Script - Referral Request',
        content: `Hey [Name]! Quick question for you.

I'm expanding my practice helping families with financial protection and planning.

Who comes to mind that might be looking for ways to protect their income or save more for the future?

Any introductions would mean a lot - I promise to take great care of anyone you send my way!`
      },
      {
        title: 'Cultivate Script - Relationship Touch',
        content: `Hey [Name]! Just wanted to check in and see how things are going with you and the family.

Saw your post about [recent event/accomplishment] - that's awesome!

No agenda here, just thinking about you. Let's catch up soon!`
      }
    ],
    checklist: `DAILY/WEEKLY ACTIVITY CHECKLIST

□ Daily Goals:
  • 50 outbound messages (mix of Hunt/Farm/Cultivate)
  • 5 meaningful conversations
  • 2 appointments set
  • Update prospect tracking

□ Weekly Goals:
  • 3 presentations delivered
  • 1 new recruit conversation
  • Review and update contact list
  • Plan next week's outreach

□ Monthly Goals:
  • Complete 1 full contact list cycle
  • 3 new recruits signed
  • 3 life applications submitted
  • $3,000 base premium`
  };

  const socialMediaContent = {
    hostToPost: `THE HOST-TO-POST SYSTEM

How it works:
1. YOU post curiosity content (you're the "host")
2. Your MENTOR/UPLINE handles the replies and conversations
3. You tag your mentor when someone shows interest
4. Mentor closes the conversation and books appointments

Benefits:
• New reps can start posting immediately
• Learn by watching experienced closers work
• Build your online presence while developing skills
• Mentor relationship stays strong

Remember: Your job is to CREATE curiosity, not to CLOSE in comments.`,
    handoffScripts: [
      {
        title: 'Comment Handoff Script',
        content: `Thanks for your interest, [Name]! Let me connect you with my mentor [Mentor Name] who can share more details. They're amazing at explaining how this works.

[Tag Mentor] - [Name] here is curious about what we do!`
      },
      {
        title: 'DM Handoff Script',
        content: `Hey [Name]! So glad you reached out. I'm actually still learning the business myself, but my mentor [Mentor Name] is incredible at explaining everything.

Mind if I connect you two? They can answer all your questions way better than I can right now!`
      }
    ],
    curiosityPosts: [
      {
        title: 'Company Expansion Hook',
        content: `Just found out our company is expanding into [area/market] and looking for entrepreneurs to help lead the growth...

If you've ever thought about building something on the side, DM me "INFO" and I'll share what we're doing.`
      },
      {
        title: 'Work From Home Hook',
        content: `3 years ago I couldn't imagine working from my phone while [doing everyday activity]...

Now it's just part of my day. Wild how things change.

Comment "HOW" if you want to know more.`
      },
      {
        title: 'Lifestyle Transformation',
        content: `The difference between last year and now:
• Then: Stressed about money
• Now: Building something that pays me even when I'm not working

Drop a 🙋 if you want to know what changed.`
      },
      {
        title: 'Results Post',
        content: `Just helped another family protect their income and start building wealth...

This is why I do what I do. 

Anyone else want to learn how to help families like this? Comment below.`
      },
      {
        title: 'Opportunity Teaser',
        content: `Looking for 3 people who:
✅ Want extra income without quitting their job
✅ Are coachable and willing to learn
✅ Have 5-10 hours per week

Sound like you? Send me "READY" in my DMs.`
      }
    ],
    postingRhythm: `POSTING RHYTHM GUIDANCE

Frequency:
• 3-5 posts per week minimum
• Mix content types: curiosity, value, lifestyle, results
• Stories: Daily activity (more casual)

Best Times to Post:
• Weekdays: 7-9am, 12-1pm, 5-7pm
• Weekends: 9-11am
• Test and track your audience's peak times

Content Balance:
• 40% Curiosity posts (generate interest)
• 30% Value posts (financial tips, education)
• 20% Lifestyle posts (behind-the-scenes, personal)
• 10% Direct opportunity posts`
  };

  const appointmentContent = {
    framework: `THE 5-STEP APPOINTMENT SETTING FRAMEWORK

1. SMALL TALK (30 seconds)
   Build rapport, ask about them personally

2. LINKER PHRASE
   "The reason I'm calling is..." or "I was thinking about you because..."

3. CURIOSITY HOOK
   Share a benefit or result without explaining everything
   "I've been helping people protect their income while building wealth..."

4. ASSUMPTIVE CLOSE
   Don't ask IF, ask WHEN
   "Would Tuesday or Thursday work better for you?"

5. CONFIRM & POST-CLOSE
   Lock in the appointment and set expectations
   "Great! I'll send you a calendar invite. Looking forward to it!"`,
    marketScripts: [
      {
        title: 'Warm Market Script',
        content: `Hey [Name]! How's everything going?

(Small talk)

Hey, the reason I'm calling - I just started working with a company that helps families protect their income and build wealth, and honestly I thought of you.

I'm not sure if it's a fit, but I'd love to share what we're doing. Takes about 20 minutes.

Would Tuesday evening or Wednesday afternoon work better?`
      },
      {
        title: 'Cold Market Script',
        content: `Hi [Name], this is [Your Name]. We haven't met yet, but I came across your profile and was impressed by [something specific].

I work with families helping them protect their income and reach their financial goals.

I'm not sure if what I do would be valuable for you, but would you be open to a quick 15-minute conversation to find out?`
      },
      {
        title: 'Referral Script',
        content: `Hey [Name]! [Referrer] suggested I reach out to you.

They mentioned you might be interested in learning more about what we do - helping families with financial protection and building wealth.

[Referrer] spoke highly of you, and I'd love to share how we might be able to help.

Do you have 20 minutes this week for a quick call?`
      }
    ],
    objections: [
      {
        objection: '"I\'m too busy right now"',
        response: 'I totally understand - that\'s exactly why I only need 20 minutes. What does your schedule look like early next week? I can work around you.'
      },
      {
        objection: '"Can you just tell me what it is?"',
        response: 'I appreciate you being direct! It\'s actually easier to show you than explain - that\'s why I suggested meeting. I promise it\'ll be worth 20 minutes of your time.'
      },
      {
        objection: '"Is this a pyramid scheme?"',
        response: 'Ha! I get that question sometimes. No, we\'re a licensed financial services company - we help families with insurance and investments. We\'ve been around for over 45 years. Want me to share more when we meet?'
      },
      {
        objection: '"I need to think about it"',
        response: 'Of course! What specifically would you like to think about? Maybe I can help answer some questions right now.'
      }
    ],
    noShowPrevention: [
      {
        title: '24-Hour Reminder',
        content: `Hey [Name]! Just wanted to confirm we're still on for tomorrow at [time].

Looking forward to sharing some strategies that could really help you.

See you then! 🙌`
      },
      {
        title: '1-Hour Reminder',
        content: `Hey [Name]! Quick reminder - we're meeting in about an hour at [time].

Here's the link/address: [details]

See you soon!`
      }
    ]
  };

  const businessOwnerContent = {
    targetList: [
      'Mortgage Brokers',
      'Real Estate Agents',
      'Accountants & CPAs',
      'Insurance Agents (P&C, Health)',
      'Financial Advisors',
      'Attorneys',
      'Chiropractors & Healthcare Providers',
      'Gym Owners & Personal Trainers',
      'Restaurant Owners',
      'Contractors & Tradespeople'
    ],
    directOutreach: {
      title: 'Business Owner Direct Outreach',
      content: `Hi [Name],

I came across your business and was impressed by what you've built.

I work with successful business owners helping them diversify their income and build something that doesn't require them to trade time for money.

Many of the professionals I work with have found this to be a great complement to their existing business.

Would you be open to a quick conversation to see if it might be a fit?`
    },
    twoGoals: {
      title: 'The "Two Goals" Framework Script',
      content: `[After rapport building]

Let me ask you something, [Name]. Most successful business owners I talk to have two main goals:

1. They want to protect what they've already built
2. They want to create income that doesn't require them to be there

Which of those resonates more with you right now?

[Listen and respond accordingly]

What we do addresses both of those - want me to show you how?`
    },
    qualifyingScale: {
      title: '1-10 Scale Qualifying Question',
      content: `[Name], on a scale of 1-10, where 1 is "not at all" and 10 is "absolutely yes"...

How open are you to looking at an opportunity that could create additional income without interfering with your current business?

[Wait for answer]`
    },
    responseScripts: [
      {
        title: 'Score 8-10 Response',
        content: `That's great to hear! Sounds like the timing might be right.

Let me ask - what specifically interests you about diversifying your income?

[Listen]

Perfect. Let's set up a time where I can show you exactly how this works. Would early next week work?`
      },
      {
        title: 'Score 5-7 Response',
        content: `I appreciate your honesty. Sounds like you're open but maybe have some questions.

What would need to be true for this to be a 9 or 10 for you?

[Listen and address concerns]

Makes sense. How about we do a quick overview so you can make a more informed decision?`
      },
      {
        title: 'Score 1-4 Response',
        content: `I appreciate you being direct with me.

Just curious - what's holding you back from being more open to it?

[Listen]

I understand. Well, if anything changes, please keep me in mind. In the meantime, I'd still love to help you with [their primary business need] if you ever need referrals.`
      }
    ]
  };

  const presentationContent = {
    formFramework: `THE FORM RAPPORT FRAMEWORK

Build connection by exploring these areas:

F - FAMILY
• "Tell me about your family..."
• "How long have you been married?"
• "How old are your kids?"

O - OCCUPATION
• "What do you do for work?"
• "How long have you been doing that?"
• "What do you enjoy most about it?"

R - RECREATION
• "What do you do for fun?"
• "Any hobbies or interests?"
• "Where did you go on your last vacation?"

M - MOTIVATION
• "What are your biggest financial goals?"
• "What keeps you up at night about money?"
• "If money wasn't an issue, what would you do differently?"

Remember: People don't care how much you know until they know how much you care.`,
    qualifyingQuestions: [
      {
        number: 1,
        question: 'Permission Question',
        script: '"Before we dive in, I want to make sure you\'re comfortable. Is it okay if I ask you some questions about your financial situation to see if we can help?"'
      },
      {
        number: 2,
        question: 'Mutual Opt-Out',
        script: '"I want this to be a no-pressure conversation. If at any point this doesn\'t seem like a fit for you, just say so - no hard feelings. And if I feel it\'s not a fit, I\'ll be honest with you too. Fair?"'
      },
      {
        number: 3,
        question: 'Financial Need Scale',
        script: '"On a scale of 1-10, how important is it for you to get your financial house in order in the next 12 months?"'
      },
      {
        number: 4,
        question: 'Time Availability (3 Tracks)',
        script: '"How much time could you realistically commit to building something on the side? Would you say: (A) 5-10 hours/week, (B) 10-20 hours/week, or (C) I\'m looking for something full-time?"'
      },
      {
        number: 5,
        question: 'Decision Authority',
        script: '"If everything we talk about makes sense to you, are you in a position to make a decision today? Or do you need to discuss with someone else first?"'
      },
      {
        number: 6,
        question: 'Eligibility & Investment',
        script: '"Just to make sure this is a fit - to get started, there\'s a small investment of [amount] for licensing and training. Is that something you\'d be able to do if this makes sense?"'
      },
      {
        number: 7,
        question: 'Start Timeline',
        script: '"If everything checks out and this feels right, when would you want to get started?"'
      }
    ],
    closingScripts: [
      {
        title: 'The Assumptive Close',
        content: `Based on everything we've discussed, it sounds like this could really help you reach your goals.

The next step is to get your paperwork started. I have some time tomorrow at 2pm or Thursday at 6pm to help you complete it - which works better?`
      },
      {
        title: 'The Summary Close',
        content: `Let me make sure I understand what's important to you:
- You want to [goal 1]
- You're looking for [goal 2]  
- And you mentioned [goal 3]

Everything we talked about today addresses all of that. Ready to get started?`
      }
    ]
  };

  const objectionContent = {
    frameworks: [
      {
        name: 'Affirm > Answer > Close',
        description: 'Acknowledge their concern, provide your answer, then move toward a close.',
        example: '"I understand why you\'d feel that way. Here\'s what I\'ve found... Based on that, does it make sense to move forward?"'
      },
      {
        name: 'Feel > Felt > Found',
        description: 'Show empathy, share a similar experience, reveal the outcome.',
        example: '"I know how you feel. I felt the same way when I first heard about this. What I found was..."'
      },
      {
        name: 'Reverse Close',
        description: 'Turn their objection into a reason to take action.',
        example: '"That\'s exactly why you should do this. Because [reason]..."'
      }
    ],
    commonObjections: [
      {
        objection: '"I don\'t have the money"',
        response: '"I understand - most people feel that way initially. Let me ask you this: if money wasn\'t an issue, would you be interested? [If yes] Great, then let\'s figure out how to make it work. Many people start by [financing option/smaller commitment]."'
      },
      {
        objection: '"I need to think about it"',
        response: '"Of course, it\'s a big decision. What specifically do you want to think about? [Listen] Let me address that right now so you have all the information you need."'
      },
      {
        objection: '"I need to talk to my spouse"',
        response: '"That makes total sense - my spouse and I make decisions together too. When can we all get on a call together? That way I can answer any questions they might have directly."'
      },
      {
        objection: '"I\'m too busy"',
        response: '"I hear that a lot, actually. Most successful people I work with felt the same way. Here\'s what they found: this actually saves time in the long run because [benefit]. What if we could find just 5 hours a week to start?"'
      },
      {
        objection: '"Is this MLM / pyramid scheme?"',
        response: '"I get why you\'d ask that. No, we\'re a licensed financial services company that\'s been around for over 45 years. We sell actual products - insurance and investments - to real clients. The team-building aspect is optional. Does that help clarify?"'
      },
      {
        objection: '"I\'m not good at sales"',
        response: '"Neither was I! The beautiful thing is, we have a complete training system. You\'ll never be alone - your upline supports you every step. Plus, you\'re not really \'selling\' - you\'re helping families. Does that feel different?"'
      },
      {
        objection: '"I\'ve tried something like this before"',
        response: '"I appreciate you being upfront about that. What happened with the previous opportunity? [Listen] That makes sense. Here\'s how what we do is different..."'
      },
      {
        objection: '"I don\'t know anyone"',
        response: '"You might be surprised! We have a process to help you identify 100+ people you already know. Plus, we\'ll teach you how to meet new people. Let me show you how that works."'
      },
      {
        objection: '"Let me do some research first"',
        response: '"Absolutely - I\'d want you to feel confident. What would you be researching? [Listen] Let me give you the direct information so you don\'t have to dig through random websites. What questions do you have?"'
      },
      {
        objection: '"The timing isn\'t right"',
        response: '"When would be the right time? [Listen] Here\'s what I\'ve found: there\'s never a \'perfect\' time. People who wait for perfect timing often never start. What if we got you started small while timing improves?"'
      },
      {
        objection: '"I\'m happy where I am"',
        response: '"That\'s great! I\'m not trying to change that. This isn\'t about leaving anything - it\'s about adding something. What if you could keep everything you have AND add an extra income stream?"'
      },
      {
        objection: '"What\'s the catch?"',
        response: '"Fair question! The \'catch\' is that it requires work. This isn\'t a get-rich-quick scheme. You\'ll need to learn, put in effort, and be consistent. But if you do that, the results can be incredible. Sound reasonable?"'
      }
    ],
    smokescreenGuidance: `HANDLING SMOKESCREENS

A smokescreen is a surface-level objection that hides the real concern.

How to identify:
• They give vague answers
• The objection doesn't match their earlier enthusiasm
• They seem uncomfortable but won't say why

How to handle:
1. Acknowledge the stated objection
2. Ask: "I sense there might be something else on your mind. What's really holding you back?"
3. Wait patiently for the real objection
4. Address the actual concern

Example:
"I appreciate you sharing that. I want to make sure I'm really helping you. Sometimes people have concerns they're not quite sure how to express. Is there anything else that's making you hesitate?"

Remember: The real objection is usually 2-3 questions deep.`
  };

  return (
    <div className="space-y-6 pb-20" data-testid="playbook-library-page">
      <div className="flex items-center gap-3">
        <BookOpen className="h-8 w-8 text-[#E31837]" />
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">Playbook & Scripts Library</h1>
          <p className="text-gray-600">Reference materials and scripts for team success</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 h-auto gap-2 bg-transparent">
          <TabsTrigger value="prospecting" className="data-[state=active]:bg-[#E31837] data-[state=active]:text-white">
            <Target className="h-4 w-4 mr-1" />
            Prospecting
          </TabsTrigger>
          <TabsTrigger value="social" className="data-[state=active]:bg-[#E31837] data-[state=active]:text-white">
            <Users className="h-4 w-4 mr-1" />
            Social Media
          </TabsTrigger>
          <TabsTrigger value="appointment" className="data-[state=active]:bg-[#E31837] data-[state=active]:text-white">
            <Calendar className="h-4 w-4 mr-1" />
            Appointments
          </TabsTrigger>
          <TabsTrigger value="business" className="data-[state=active]:bg-[#E31837] data-[state=active]:text-white">
            <Briefcase className="h-4 w-4 mr-1" />
            Business Owners
          </TabsTrigger>
          <TabsTrigger value="presentation" className="data-[state=active]:bg-[#E31837] data-[state=active]:text-white">
            <MessageSquare className="h-4 w-4 mr-1" />
            Presentations
          </TabsTrigger>
          <TabsTrigger value="objections" className="data-[state=active]:bg-[#E31837] data-[state=active]:text-white">
            <Shield className="h-4 w-4 mr-1" />
            Objections
          </TabsTrigger>
        </TabsList>

        {/* Prospecting Tab */}
        <TabsContent value="prospecting" className="mt-6">
          <SectionHeader icon={Target} title="Prospecting" description="Market definitions, the Hunt/Farm/Cultivate framework, and daily activity checklists" />
          
          <h3 className="text-lg font-semibold mb-3 text-[#333333]">Market Definitions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {prospectingContent.definitions.map((def, idx) => (
              <Card key={idx} className="p-4">
                <h4 className="font-bold text-[#E31837]">{def.term}</h4>
                <p className="text-sm text-gray-600 mt-1">{def.description}</p>
              </Card>
            ))}
          </div>

          <ScriptCard title="Hunt/Farm/Cultivate Framework" content={prospectingContent.framework} />
          
          <h3 className="text-lg font-semibold mb-3 mt-8 text-[#333333]">Example Scripts by Phase</h3>
          {prospectingContent.scripts.map((script, idx) => (
            <ScriptCard key={idx} title={script.title} content={script.content} />
          ))}

          <ScriptCard title="Daily/Weekly Activity Checklist" content={prospectingContent.checklist} />
        </TabsContent>

        {/* Social Media Tab */}
        <TabsContent value="social" className="mt-6">
          <SectionHeader icon={Users} title="Social Media" description="Host-to-post system, curiosity posts, and posting strategies" />
          
          <ScriptCard title="The Host-to-Post System" content={socialMediaContent.hostToPost} />
          
          <h3 className="text-lg font-semibold mb-3 mt-8 text-[#333333]">Handoff Scripts</h3>
          {socialMediaContent.handoffScripts.map((script, idx) => (
            <ScriptCard key={idx} title={script.title} content={script.content} />
          ))}

          <h3 className="text-lg font-semibold mb-3 mt-8 text-[#333333]">Curiosity Post Templates</h3>
          {socialMediaContent.curiosityPosts.map((post, idx) => (
            <ScriptCard key={idx} title={post.title} content={post.content} category="Post Template" />
          ))}

          <ScriptCard title="Posting Rhythm Guidance" content={socialMediaContent.postingRhythm} />
        </TabsContent>

        {/* Appointment Tab */}
        <TabsContent value="appointment" className="mt-6">
          <SectionHeader icon={Calendar} title="Appointment Setting" description="5-step framework, scripts by market, and no-show prevention" />
          
          <ScriptCard title="The 5-Step Appointment Setting Framework" content={appointmentContent.framework} />
          
          <h3 className="text-lg font-semibold mb-3 mt-8 text-[#333333]">Scripts by Market Type</h3>
          {appointmentContent.marketScripts.map((script, idx) => (
            <ScriptCard key={idx} title={script.title} content={script.content} />
          ))}

          <h3 className="text-lg font-semibold mb-3 mt-8 text-[#333333]">Common Booking Objections</h3>
          {appointmentContent.objections.map((obj, idx) => (
            <Card key={idx} className="p-4 mb-3">
              <p className="font-semibold text-red-600">{obj.objection}</p>
              <p className="text-sm text-gray-700 mt-2">{obj.response}</p>
            </Card>
          ))}

          <h3 className="text-lg font-semibold mb-3 mt-8 text-[#333333]">No-Show Prevention</h3>
          {appointmentContent.noShowPrevention.map((script, idx) => (
            <ScriptCard key={idx} title={script.title} content={script.content} />
          ))}
        </TabsContent>

        {/* Business Owner Tab */}
        <TabsContent value="business" className="mt-6">
          <SectionHeader icon={Briefcase} title="Business Owner Recruiting" description="Target list, outreach scripts, and qualifying frameworks" />
          
          <Card className="p-4 mb-6">
            <h3 className="font-semibold text-[#333333] mb-3">Target Business Owner List</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {businessOwnerContent.targetList.map((target, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm">{target}</span>
                </div>
              ))}
            </div>
          </Card>

          <ScriptCard title={businessOwnerContent.directOutreach.title} content={businessOwnerContent.directOutreach.content} />
          <ScriptCard title={businessOwnerContent.twoGoals.title} content={businessOwnerContent.twoGoals.content} />
          <ScriptCard title={businessOwnerContent.qualifyingScale.title} content={businessOwnerContent.qualifyingScale.content} />
          
          <h3 className="text-lg font-semibold mb-3 mt-8 text-[#333333]">Response Scripts by Score</h3>
          {businessOwnerContent.responseScripts.map((script, idx) => (
            <ScriptCard key={idx} title={script.title} content={script.content} />
          ))}
        </TabsContent>

        {/* Presentation Tab */}
        <TabsContent value="presentation" className="mt-6">
          <SectionHeader icon={MessageSquare} title="Presentation & Qualifying" description="FORM framework, qualifying questions, and closing scripts" />
          
          <ScriptCard title="FORM Rapport Framework" content={presentationContent.formFramework} />
          
          <h3 className="text-lg font-semibold mb-3 mt-8 text-[#333333]">The 7 Qualifying Questions</h3>
          {presentationContent.qualifyingQuestions.map((q, idx) => (
            <Card key={idx} className="p-4 mb-3">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-8 w-8 rounded-full bg-[#E31837] text-white flex items-center justify-center font-bold">
                  {q.number}
                </div>
                <h4 className="font-semibold text-[#333333]">{q.question}</h4>
              </div>
              <p className="text-sm text-gray-700 ml-11">{q.script}</p>
            </Card>
          ))}

          <h3 className="text-lg font-semibold mb-3 mt-8 text-[#333333]">Closing Scripts</h3>
          {presentationContent.closingScripts.map((script, idx) => (
            <ScriptCard key={idx} title={script.title} content={script.content} />
          ))}
        </TabsContent>

        {/* Objections Tab */}
        <TabsContent value="objections" className="mt-6">
          <SectionHeader icon={Shield} title="Objection Handling" description="Core frameworks and responses to common objections" />
          
          <h3 className="text-lg font-semibold mb-3 text-[#333333]">Core Frameworks</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {objectionContent.frameworks.map((fw, idx) => (
              <Card key={idx} className="p-4">
                <h4 className="font-bold text-[#E31837]">{fw.name}</h4>
                <p className="text-sm text-gray-600 mt-1">{fw.description}</p>
                <p className="text-xs text-gray-500 mt-2 italic">{fw.example}</p>
              </Card>
            ))}
          </div>

          <h3 className="text-lg font-semibold mb-3 text-[#333333]">Common Objections & Responses</h3>
          {objectionContent.commonObjections.map((obj, idx) => (
            <Card key={idx} className="p-4 mb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="font-semibold text-red-600">{obj.objection}</p>
                  <p className="text-sm text-gray-700 mt-2">{obj.response}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(obj.response);
                    toast.success('Response copied!');
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}

          <ScriptCard title="Handling Smokescreens" content={objectionContent.smokescreenGuidance} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
